import React from 'react'
import {generatePath} from 'react-router'
import {Link as RouterLink} from 'react-router-dom'
import {CompartmentLink} from '../../common/router_links/CompartmentLink'
import {ProductLink} from '../../common/router_links/ProductLink'
import {UserLink} from '../../common/router_links/UserLink'
import {Routes} from '../../common/Routes'
import {Product} from '../../common/types/product'
import {Rental} from '../../common/types/rental'
import {formatISO, toDisplayDateTime} from '../../common/utils/dateUtils'
import {resolveBoxDisplayName} from '../../common/utils/resolveBoxDisplayName'
import {Link} from '../../webui/Link'
import {LoadingSpinnerContainer} from '../../webui/progress/LoadingSpinnerContainer'
import {SortablePaginatedTable} from '../../webui/table/sortable-paginated-table/SortablePaginatedTable'
import {TableHeadCell} from '../../webui/table/sortable-paginated-table/TableHeadCell'
import {useAllOngoingRentals} from './gql/useAllOngoingRentals'
import {getRentTime} from '../../gql/transformRental'

interface Props {}

interface AllRentalData {
  id: string
  box: Rental['box']
  compartment: Rental['compartment']
  product: Product
  maxRentTimeInMinutes: number
  numberOfFailedOpenings: number
  numberOfSuccessfulOpenings: number
  rentBy: Rental['rentBy']
  rentTime: Rental['rentTime']
  rentStart?: string
  rentEnd?: string
  commentsCount: number
}

const createTableHeadCells: TableHeadCell<AllRentalData>[] = [
  {
    id: 'rentStart',
    label: 'Started',
    render: (value) => toDisplayDateTime(value['rentStart']),
    maxWidth: 120,
  },
  {
    id: 'commentsCount',
    label: 'Comments',
    render: (value) => value['commentsCount'],
    maxWidth: 40,
  },
  {
    id: 'box',
    label: 'Box',
    render: (row) => (
      <Link
        component={RouterLink}
        to={generatePath(Routes.BOX, {
          id: row['box'].id,
        })}
      >
        {resolveBoxDisplayName(row['box'])}
      </Link>
    ),
    minWidth: 50,
  },
  {
    id: 'compartment',
    label: 'Comp.',
    render: (row) => <CompartmentLink boxId={row.box.id} compartmentId={row.compartment.id} linkText={row.compartment.name ?? ''} />,
    maxWidth: 35,
  },
  {
    id: 'product',
    label: 'Product',
    render: (value) => <ProductLink product={value['product']} />,
  },
  {
    id: 'rentBy',
    label: 'Rented by',
    render: (row) => <UserLink userId={row.rentBy.id} linkText={row['rentBy'].email || 'Anonymized user'} />,
    maxWidth: 200,
  },
  {
    id: 'rentTime',
    label: 'Rent time',
    render: (value) => {
      const rentTime = getRentTime(value['rentStart'], formatISO(new Date()))
      const maxRentTime = value['maxRentTimeInMinutes']
      const isOver = rentTime != null && maxRentTime != null && rentTime > maxRentTime

      return <span style={{color: isOver ? 'red' : 'inherit'}}>{rentTime ?? ''}</span>
    },
    cellTooltip: (value) => {
      const rentTime = getRentTime(value['rentStart'], formatISO(new Date()))
      if (rentTime == null) return undefined
      return rentTime >= 60 ? `${Math.floor(rentTime / 60)}h ${rentTime % 60}m` : `${rentTime}m`
    },
    cellTooltipPlacement: {placement: 'top', offset: [50, -30]},
  },
  {
    id: 'maxRentTimeInMinutes',
    label: 'Max rent time',
    render: (value) => value['maxRentTimeInMinutes'],
    maxWidth: 30,
  },
  {
    id: 'numberOfSuccessfulOpenings',
    label: 'Successful openings',
    render: (value) => value['numberOfSuccessfulOpenings'],
    maxWidth: 40,
  },
  {
    id: 'numberOfFailedOpenings',
    label: 'Failed openings',
    render: (value) => value['numberOfFailedOpenings'],
    maxWidth: 40,
  },
]

const INITIAL_ROWS_PER_PAGE = 20

const createRows = (rentals: Rental[]): AllRentalData[] =>
  rentals.map((rental) => {
    const commentsCount = (rental as any).internalComments?.length ?? ''
    return {
      id: rental.id,
      box: rental.box,
      compartment: rental.compartment,
      product: rental.product,
      rentBy: rental.rentBy,
      rentStart: rental.rentStart,
      rentEnd: rental.rentEnd,
      rentTime: rental.rentTime,
      maxRentTimeInMinutes: rental.product.maxRentTimeInMinutes,
      numberOfFailedOpenings: rental.numberOfFailedOpenings,
      numberOfSuccessfulOpenings: rental.numberOfSuccessfulOpenings,
      commentsCount,
    }
  })

export const OngoingRentalsTable: React.FC<Props> = () => {
  const {data: ongoingRentals, loading, error} = useAllOngoingRentals()

  if (loading) {
    return <LoadingSpinnerContainer />
  }
  if (error) {
    return <div>{error.message}</div>
  }

  return (
    <SortablePaginatedTable
      title={'Ongoing rentals'}
      initialRowsPerPage={INITIAL_ROWS_PER_PAGE}
      initialOrderByKey={'rentStart'}
      initialOrder={'desc'}
      rows={createRows(ongoingRentals)}
      tableHeadCells={createTableHeadCells}
      rowLinkTo={(row) => generatePath(Routes.RENTAL, {id: row.id})}
      maxTableWidth={1900}
    />
  )
}
